import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';
import { ArrowLeft, Mail } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [expiryMinutes, setExpiryMinutes] = useState(30);
  const { toast } = useToast();
  const { t } = useLanguage();

  // Fetch password reset expiry setting
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('password_reset_expiry_minutes')
          .single();

        if (!error && data?.password_reset_expiry_minutes) {
          setExpiryMinutes(data.password_reset_expiry_minutes);
        }
      } catch (error) {
        console.error('Error fetching password reset expiry:', error);
        // Keep default value of 30 minutes
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: t('auth.forgot_password_error_title'),
        description: t('auth.forgot_password_error_email'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Calculate expiry time in seconds
      const expirySeconds = expiryMinutes * 60;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
        options: {
          emailRedirectTo: `${window.location.origin}/reset-password`,
          // Note: Supabase Auth uses seconds for expiry
          data: {
            expirySeconds: expirySeconds
          }
        }
      });

      if (error) throw error;

      setEmailSent(true);
      toast({
        title: t('auth.forgot_password_success_title'),
        description: t('auth.forgot_password_success_desc'),
      });
    } catch (error: any) {
      toast({
        title: t('auth.forgot_password_error_title'),
        description: error.message || t('auth.forgot_password_error_general'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4">
      <div className="w-full max-w-md">
        {/* Back to login link */}
        <Link 
          to="/auth" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('auth.back_to_login')}
        </Link>

        {/* Card */}
        <div className="bg-card border rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">{t('auth.forgot_password_title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('auth.forgot_password_subtitle')}
            </p>
          </div>

          {!emailSent ? (
            /* Email form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? t('auth.sending') : t('auth.send_reset_link')}
              </Button>
            </form>
          ) : (
            /* Success message */
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  {t('auth.forgot_password_email_sent')}
                </p>
              </div>
              
              <p className="text-xs text-muted-foreground">
                {t('auth.forgot_password_check_spam')}
              </p>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
              >
                {t('auth.send_another_email')}
              </Button>
            </div>
          )}
        </div>

        {/* Additional help */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            {t('auth.forgot_password_help')}{' '}
            <a 
              href="mailto:contact@nazandoo.com" 
              className="text-primary hover:underline"
            >
              contact@nazandoo.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
